import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { API_ORIGIN } from '../config/api';
import { ADMIN_LOGIN_RETURNING_KEY } from '../constants/adminLoginStorage';

export default function AutoLogin() {
  const [message, setMessage] = useState('Setting up your account...');
  const [isError, setIsError] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // strict-mode double-fire guard
    ran.current = true;

    const token = new URLSearchParams(window.location.search).get('token');

    if (!token) {
      setIsError(true);
      setMessage('No token found. Please go back and try again.');
      return;
    }

    const doAutoLogin = async () => {
      try {
        const url = `${API_ORIGIN}/api/auth/auto-login`;
        console.log('[AutoLogin] POST', url, 'tokenLen=', token?.length);
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const bodyText = await res.text();
        console.log('[AutoLogin] response', res.status, bodyText.slice(0, 300));
        // Server errors can come back as an HTML page — don't crash on JSON.parse.
        let data = {};
        try {
          data = bodyText ? JSON.parse(bodyText) : {};
        } catch {
          data = {};
        }

        if (!res.ok || !data.accessToken) {
          throw new Error(
            data.message ||
              (res.status === 401
                ? 'This login link is invalid or has expired. Please log in with your email and password.'
                : `Auto-login failed (${res.status}). Please log in with your email and password.`),
          );
        }

        // Mirror exactly what Login.js does so App.js picks up the session.
        // Support impersonation sessions are 2h on the JWT; cookie can outlive that.
        const cookieOpts = {
          expires: 1,
          path: '/',
          sameSite: 'strict',
          secure: window.location.protocol === 'https:',
        };
        localStorage.setItem(ADMIN_LOGIN_RETURNING_KEY, '1');
        Cookies.set('accessToken', data.accessToken, cookieOpts);
        if (data.id) Cookies.set('userId', String(data.id), cookieOpts);
        if (data.company?.id) Cookies.set('companyId', data.company.id, cookieOpts);
        if (data.company?.companyName) Cookies.set('companyName', data.company.companyName, cookieOpts);
        if (data.name) Cookies.set('userName', data.name, cookieOpts);
        localStorage.setItem('userRoles', JSON.stringify(data.roles || []));
        localStorage.setItem('userPermissions', JSON.stringify(data.permissions || []));
        if (data.loginMethod === 'support_impersonation') {
          sessionStorage.setItem(
            'supportSessionCompany',
            data.company?.companyName || '',
          );
        }

        // Full reload so App.js re-runs its useEffect and picks up the new cookie
        window.location.replace('/');
      } catch (err) {
        setIsError(true);
        setMessage(err.message || 'Something went wrong. Please try logging in manually.');
      }
    };

    doAutoLogin();
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '12px' }}>
      {!isError && (
        <div style={{
          width: 32,
          height: 32,
          border: '3px solid #3b82f6',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <p style={{ fontSize: 16, color: isError ? '#ef4444' : '#64748b', fontWeight: 600, margin: 0 }}>
        {message}
      </p>
      {isError && (
        <a href="/login" style={{ color: '#3b82f6', fontSize: 14 }}>Go to Login</a>
      )}
    </div>
  );
}
